// Chart.js wiring for chart blocks. Chart.js is loaded on first use in its own
// chunk, so customers who never ask for a graph never download it.

const SERIES_COLORS = ['#5d7afd', '#ffb74d', '#4caf85', '#f06a6a', '#9b7bf2', '#42b8d6'];

// Pie slices use only these colors, in this order (largest slice first). The last
// one is kept for "אחר", which the prompt always adds as the last slice.
const PIE_COLORS = [
  '#00C070', '#5D7AFD', '#F34F8A', '#B4438E', '#FFCF03',
  '#FF7E14', '#205441', '#142AA0', '#D3B019', '#A5130A',
  '#858C9E',
];
const OTHER_SLICE_LABEL = 'אחר';
const OTHER_SLICE_COLOR = PIE_COLORS[PIE_COLORS.length - 1];

// Bars get rounded top corners only; Chart.js shrinks the radius to fit a narrow bar.
const BAR_BORDER_RADIUS = { topLeft: 44, topRight: 44, bottomLeft: 0, bottomRight: 0 };

// The palette in order, "אחר" always in its own color. The prompt caps a pie at
// 10 slices plus "אחר"; a longer one repeats the palette rather than leave it.
export function pieColors(labels) {
  let colorIndex = 0;
  return labels.map(label => {
    if (label.trim() === OTHER_SLICE_LABEL) { return OTHER_SLICE_COLOR; }
    const color = PIE_COLORS[colorIndex % PIE_COLORS.length];
    colorIndex += 1;
    return color;
  });
}

const NUMBER_FORMAT = new Intl.NumberFormat('he-IL', { maximumFractionDigits: 2 });

export const formatChartNumber = value => NUMBER_FORMAT.format(value);

let chartPromise = null;

// Resolves to the Chart constructor with only the pieces chart blocks use registered.
export function loadChart() {
  if (!chartPromise) {
    chartPromise = import(/* webpackChunkName: "chat-chart" */ './chat-chart-library')
      .then(({ default: Chart }) => Chart)
      .catch(error => {
        // Let a later chart retry, e.g. after a flaky network drops the chunk.
        chartPromise = null;
        throw error;
      });
  }
  return chartPromise;
}

// The chart's data as a normalized table (see tableOf in chat-structured.js),
// for when the block carries no table of its own: one row per label, one
// column per series, missing values left empty.
export function tableFromChart({ title, labels, datasets }) {
  return {
    caption: title,
    headers: ['', ...datasets.map(dataset => dataset.label)],
    rows: labels.map((label, i) => [
      label,
      ...datasets.map(dataset => (dataset.data[i] === null ? '' : formatChartNumber(dataset.data[i]))),
    ]),
  };
}

// Slices smaller than this share of the pie get no number: it wouldn't fit and
// would cover the neighboring slices. The value is still in the tooltip and legend.
export const PIE_LABEL_MIN_SHARE = 0.05;

// Draws each slice's value on the slice itself, in plain white, two
// thirds of the way out from the center. Written as a tiny inline Chart.js plugin to avoid adding a dependency.
export function pieSliceLabels(fontFamily) {
  return {
    id: 'pieSliceLabels',
    afterDatasetsDraw(chart) {
      const values = chart.data.datasets[0].data;
      const total = values.reduce((sum, value, i) => (
        value > 0 && chart.getDataVisibility(i) ? sum + value : sum), 0);
      if (!total) { return; }
      const { ctx } = chart;
      ctx.save();
      ctx.font = `600 12px ${fontFamily || 'sans-serif'}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffffff';
      chart.getDatasetMeta(0).data.forEach((arc, i) => {
        const value = values[i];
        if (!(value > 0) || !chart.getDataVisibility(i) || value / total < PIE_LABEL_MIN_SHARE) { return; }
        const { x, y, startAngle, endAngle, outerRadius } = arc.getProps(['x', 'y', 'startAngle', 'endAngle', 'outerRadius'], true);
        const angle = (startAngle + endAngle) / 2;
        const radius = outerRadius * 0.65;
        const text = formatChartNumber(value);
        ctx.fillText(text, x + Math.cos(angle) * radius, y + Math.sin(angle) * radius);
      });
      ctx.restore();
    },
  };
}

// Chart.js config for a normalized chart (see chartOf in chat-structured.js).
export function buildChartConfig({ chartType, title, labels, datasets }, { fontFamily } = {}) {
  const isPie = chartType === 'pie';
  const font = fontFamily ? { family: fontFamily } : undefined;
  return {
    type: chartType,
    plugins: isPie ? [pieSliceLabels(fontFamily)] : [],
    data: {
      labels,
      datasets: datasets.map((dataset, i) => {
        const color = SERIES_COLORS[i % SERIES_COLORS.length];
        return isPie
          ? { ...dataset, backgroundColor: pieColors(labels) }
          : { ...dataset, backgroundColor: color, borderColor: color, borderRadius: chartType === 'bar' ? BAR_BORDER_RADIUS : 0, tension: 0.2 };
      }),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 400 },
      font,
      plugins: {
        title: { display: Boolean(title), text: title, font: { ...font, size: 14, weight: '600' }, color: '#202125' },
        // A pie's legend goes beside it (left), so a long list of slices doesn't squeeze the circle.
        legend: isPie
          ? {
            display: true,
            position: 'left',
            rtl: true,
            textDirection: 'rtl',
            labels: { font: { ...font, size: 12 }, boxWidth: 12, boxHeight: 12, padding: 8 },
          }
          : { display: datasets.length > 1, rtl: true, textDirection: 'rtl', labels: { font } },
        tooltip: {
          rtl: true,
          textDirection: 'rtl',
          callbacks: {
            label: context => {
              const value = isPie ? context.parsed : context.parsed.y;
              const name = isPie ? context.label : context.dataset.label;
              return `${name}: ${formatChartNumber(value)}`;
            },
          },
        },
      },
      scales: isPie ? {} : {
        x: { ticks: { font }, grid: { display: false } },
        y: { ticks: { font, callback: value => formatChartNumber(value) }, beginAtZero: chartType === 'bar' },
      },
    },
  };
}
